import { useState, useEffect, createContext, useContext } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Building2, 
  MessageSquare, 
  Calendar,
  Menu,
  Plus,
  Pencil,
  Trash2,
  Users,
  Activity,
  Clock,
  LogOut,
  User,
  Key,
  BarChart3,
  TrendingUp,
  Eye,
  MousePointer,
  ArrowRight,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AdminLogin from "./AdminLogin";

const API = `${window.location.origin}/api`;
const KINESERVE_LOGO = "https://customer-assets.emergentagent.com/job_kineserve-app/artifacts/7792rvix_KineServe_Logo_Dark.svg";

// Auth Context
const AuthContext = createContext(null);

function useAuth() {
  return useContext(AuthContext);
}

// Create axios instance with auth
function createAuthAxios(token) {
  const instance = axios.create({
    baseURL: API,
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return instance;
}

// Sidebar Navigation
function Sidebar({ isOpen, onClose, user, onLogout }) {
  const location = useLocation();
  
  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/admin/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/admin/tenants", icon: Building2, label: "Tenants" },
    { path: "/admin/demo-requests", icon: Calendar, label: "Demo Requests" },
    { path: "/admin/contacts", icon: MessageSquare, label: "Contacts" },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50
        transform transition-transform duration-300 flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={KINESERVE_LOGO} 
              alt="KineServe" 
              className="h-8"
              onError={(e) => {
                e.target.outerHTML = '<div class="flex items-center gap-2"><div class="bg-slate-900 text-white px-2 py-1 font-bold">KS</div><span class="font-bold text-slate-900">KineServe</span></div>';
              }}
            />
          </Link>
          <p className="text-xs text-slate-500 mt-2">Admin Panel</p>
        </div>
        
        <nav className="p-4 flex-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`
                flex items-center gap-3 px-4 py-3 mb-1 transition-colors
                ${isActive(item.path, item.exact)
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
                }
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        
        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-100 transition-colors rounded">
                <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                  <User size={16} className="text-slate-600" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {user?.name || user?.email?.split('@')[0]}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/admin/change-password" className="flex items-center gap-2">
                  <Key size={16} />
                  Change Password
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-red-600">
                <LogOut size={16} className="mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Link 
            to="/" 
            className="block text-center text-sm text-slate-500 hover:text-slate-900 transition-colors mt-4"
          >
            Back to Website
          </Link>
        </div>
      </aside>
    </>
  );
}

// Dashboard Page
function Dashboard({ authAxios }) {
  const [stats, setStats] = useState({
    total_tenants: 0,
    active_tenants: 0,
    pending_tenants: 0,
    recent_demo_requests: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await authAxios.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: "Total Tenants", value: stats.total_tenants, icon: Building2, color: "text-blue-600" },
    { label: "Active Tenants", value: stats.active_tenants, icon: Activity, color: "text-green-600" },
    { label: "Pending Setup", value: stats.pending_tenants, icon: Clock, color: "text-amber-600" },
    { label: "Demo Requests", value: stats.recent_demo_requests, icon: Users, color: "text-purple-600" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 mt-1">Overview of your KineServe tenants</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div 
            key={stat.label}
            className="stat-card animate-fade-in-up"
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-card-value">{loading ? "-" : stat.value}</p>
                <p className="stat-card-label">{stat.label}</p>
              </div>
              <div className={`p-2 bg-slate-100 ${stat.color}`}>
                <stat.icon size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-6 bg-white border border-slate-200">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link to="/admin/tenants">
            <Button className="bg-slate-900 text-white hover:bg-slate-800">
              <Plus size={18} className="mr-2" />
              Add New Tenant
            </Button>
          </Link>
          <Link to="/admin/demo-requests">
            <Button variant="outline">
              View Demo Requests
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Tenants Management Page
function TenantsPage({ authAxios }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subdomain: "",
    contact_email: "",
    contact_phone: "",
    status: "active"
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await authAxios.get("/tenants");
      setTenants(response.data);
    } catch (error) {
      toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTenant) {
        await authAxios.put(`/tenants/${selectedTenant.id}`, formData);
        toast.success("Tenant updated successfully");
      } else {
        await authAxios.post("/tenants", formData);
        toast.success("Tenant created successfully");
      }
      setModalOpen(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save tenant");
    }
  };

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      subdomain: tenant.subdomain,
      contact_email: tenant.contact_email || "",
      contact_phone: tenant.contact_phone || "",
      status: tenant.status
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await authAxios.delete(`/tenants/${selectedTenant.id}`);
      toast.success("Tenant deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (error) {
      toast.error("Failed to delete tenant");
    }
  };

  const resetForm = () => {
    setSelectedTenant(null);
    setFormData({
      name: "",
      subdomain: "",
      contact_email: "",
      contact_phone: "",
      status: "active"
    });
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "badge-active",
      inactive: "badge-inactive",
      pending: "badge-pending"
    };
    return <span className={`badge ${styles[status] || styles.pending}`}>{status}</span>;
  };

  return (
    <div data-testid="tenants-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tenants</h1>
          <p className="text-slate-600 mt-1">Manage your KineServe tenant subdomains</p>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-slate-900 text-white hover:bg-slate-800"
          data-testid="create-tenant-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Tenant
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : tenants.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No tenants yet</p>
            <Button 
              onClick={openCreateModal}
              className="mt-4 bg-slate-900 text-white hover:bg-slate-800"
            >
              Create Your First Tenant
            </Button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tenant Name</th>
                <th>Subdomain</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                  <td className="font-medium text-slate-900">{tenant.name}</td>
                  <td>
                    <code className="text-sm bg-slate-100 px-2 py-1">
                      {tenant.subdomain}.kineserve.com
                    </code>
                  </td>
                  <td className="text-slate-600 text-sm">
                    {tenant.contact_email || "-"}
                  </td>
                  <td>{getStatusBadge(tenant.status)}</td>
                  <td className="text-slate-500 text-sm">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(tenant)}
                        data-testid={`edit-tenant-${tenant.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-tenant-${tenant.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedTenant ? "Edit Tenant" : "Create New Tenant"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Grey Bull Technologies"
                  required
                  data-testid="tenant-name-input"
                />
              </div>
              <div>
                <Label htmlFor="subdomain">Subdomain *</Label>
                <div className="flex items-center">
                  <Input
                    id="subdomain"
                    value={formData.subdomain}
                    onChange={(e) => setFormData({...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    placeholder="gbt"
                    required
                    className="rounded-r-none"
                    data-testid="tenant-subdomain-input"
                  />
                  <span className="px-3 py-2 bg-slate-100 border border-l-0 border-slate-200 text-slate-600 text-sm whitespace-nowrap">
                    .kineserve.com
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Only lowercase letters, numbers, and hyphens allowed
                </p>
              </div>
              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  placeholder="admin@company.com"
                  data-testid="tenant-email-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="+91 123 456 7890"
                  data-testid="tenant-phone-input"
                />
              </div>
              {selectedTenant && (
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({...formData, status: value})}
                  >
                    <SelectTrigger data-testid="tenant-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-tenant-btn">
                {selectedTenant ? "Update" : "Create"} Tenant
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tenant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedTenant?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Demo Requests Page
function DemoRequestsPage({ authAxios }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await authAxios.get("/demo-requests");
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to load demo requests");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="demo-requests-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Demo Requests</h1>
        <p className="text-slate-600 mt-1">View incoming demo requests</p>
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No demo requests yet</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td className="font-medium text-slate-900">{request.name}</td>
                  <td>{request.company}</td>
                  <td className="text-blue-600">{request.email}</td>
                  <td>{request.phone || "-"}</td>
                  <td>
                    <span className="badge badge-pending">{request.status}</span>
                  </td>
                  <td className="text-slate-500 text-sm">
                    {new Date(request.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Contact Submissions Page
function ContactsPage({ authAxios }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await authAxios.get("/contacts");
      setContacts(response.data);
    } catch (error) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contacts-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Contact Submissions</h1>
        <p className="text-slate-600 mt-1">Messages from the contact form</p>
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : contacts.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No contact submissions yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {contacts.map((contact) => (
              <div key={contact.id} className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-medium text-slate-900">{contact.name}</h3>
                    <p className="text-sm text-blue-600">{contact.email}</p>
                    {contact.company && (
                      <p className="text-sm text-slate-500">{contact.company}</p>
                    )}
                  </div>
                  <span className="text-xs text-slate-500">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-slate-600 mt-2">{contact.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Change Password Page
function ChangePasswordPage({ authAxios }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (formData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      await authAxios.post("/auth/change-password", {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      toast.success("Password changed successfully");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="change-password-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Change Password</h1>
        <p className="text-slate-600 mt-1">Update your admin password</p>
      </div>

      <div className="max-w-md bg-white border border-slate-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="current_password">Current Password</Label>
            <Input
              id="current_password"
              type="password"
              value={formData.current_password}
              onChange={(e) => setFormData({...formData, current_password: e.target.value})}
              required
              data-testid="current-password-input"
            />
          </div>
          <div>
            <Label htmlFor="new_password">New Password</Label>
            <Input
              id="new_password"
              type="password"
              value={formData.new_password}
              onChange={(e) => setFormData({...formData, new_password: e.target.value})}
              required
              minLength={8}
              data-testid="new-password-input"
            />
            <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
          </div>
          <div>
            <Label htmlFor="confirm_password">Confirm New Password</Label>
            <Input
              id="confirm_password"
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
              required
              data-testid="confirm-password-input"
            />
          </div>
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="change-password-btn"
            >
              {loading ? "Changing..." : "Change Password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Analytics Page
function AnalyticsPage({ authAxios }) {
  const [analytics, setAnalytics] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, conversionsRes] = await Promise.all([
        authAxios.get(`/analytics/overview?days=${period}`),
        authAxios.get(`/analytics/conversions?days=${period}`)
      ]);
      setAnalytics(analyticsRes.data);
      setConversions(conversionsRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, maxValue, label }) => {
    if (!data || data.length === 0) return <p className="text-slate-500 text-sm">No data available</p>;
    const max = maxValue || Math.max(...data.map(d => d.count), 1);
    return (
      <div className="space-y-2">
        {data.slice(-14).map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <span className="text-xs text-slate-500 w-20 truncate">{item.date?.slice(5) || item.page}</span>
            <div className="flex-1 bg-slate-100 h-6 rounded overflow-hidden">
              <div 
                className="h-full bg-slate-700 transition-all duration-300"
                style={{ width: `${(item.count / max) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-slate-700 w-8 text-right">{item.count}</span>
          </div>
        ))}
      </div>
    );
  };

  // Funnel component
  const LeadFunnel = ({ funnel }) => {
    if (!funnel) return null;
    const steps = [
      { label: "Visitors", value: funnel.visitors, icon: Eye, color: "bg-blue-500" },
      { label: "Contacts", value: funnel.contacts, icon: MessageSquare, color: "bg-purple-500" },
      { label: "Demo Requests", value: funnel.demo_requests, icon: Calendar, color: "bg-amber-500" },
      { label: "Trial Requests", value: funnel.trial_requests, icon: FileText, color: "bg-green-500" },
      { label: "Tenants", value: funnel.tenants, icon: Building2, color: "bg-slate-700" },
    ];
    
    return (
      <div className="flex flex-col gap-2">
        {steps.map((step, idx) => (
          <div key={step.label} className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded flex items-center justify-center ${step.color} text-white`}>
              <step.icon size={18} />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-slate-700">{step.label}</span>
                <span className="text-sm font-bold text-slate-900">{step.value}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded overflow-hidden">
                <div 
                  className={`h-full ${step.color} transition-all duration-500`}
                  style={{ width: `${funnel.visitors > 0 ? Math.max((step.value / funnel.visitors) * 100, step.value > 0 ? 5 : 0) : 0}%` }}
                />
              </div>
            </div>
            {idx < steps.length - 1 && (
              <ArrowRight size={16} className="text-slate-300 ml-2" />
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div data-testid="analytics-page" className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div data-testid="analytics-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Analytics</h1>
          <p className="text-slate-600 mt-1">Track visitors, conversions, and lead funnel</p>
        </div>
        <Select value={period.toString()} onValueChange={(v) => setPeriod(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-card-value">{analytics?.total_pageviews || 0}</p>
              <p className="stat-card-label">Page Views</p>
            </div>
            <div className="p-2 bg-blue-50 text-blue-600 rounded">
              <Eye size={24} />
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-card-value">{analytics?.unique_visitors || 0}</p>
              <p className="stat-card-label">Unique Visitors</p>
            </div>
            <div className="p-2 bg-purple-50 text-purple-600 rounded">
              <Users size={24} />
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-card-value">{analytics?.conversions?.total || 0}</p>
              <p className="stat-card-label">Total Conversions</p>
            </div>
            <div className="p-2 bg-green-50 text-green-600 rounded">
              <TrendingUp size={24} />
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-card-value">{analytics?.conversions?.conversion_rate || 0}%</p>
              <p className="stat-card-label">Conversion Rate</p>
            </div>
            <div className="p-2 bg-amber-50 text-amber-600 rounded">
              <MousePointer size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Page Views Over Time */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Page Views (Last 14 Days)</h3>
          <SimpleBarChart data={analytics?.pageviews_by_day} />
        </div>

        {/* Lead Funnel */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Lead Funnel</h3>
          <LeadFunnel funnel={analytics?.funnel} />
        </div>
      </div>

      {/* Conversions & Sources Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversion Breakdown */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Conversion Breakdown</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
              <div className="flex items-center gap-3">
                <Calendar size={18} className="text-amber-600" />
                <span className="text-sm text-slate-700">Demo Requests</span>
              </div>
              <span className="font-bold text-slate-900">{analytics?.conversions?.demo_requests || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-green-600" />
                <span className="text-sm text-slate-700">Trial Requests</span>
              </div>
              <span className="font-bold text-slate-900">{analytics?.conversions?.trial_requests || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
              <div className="flex items-center gap-3">
                <MessageSquare size={18} className="text-purple-600" />
                <span className="text-sm text-slate-700">Contacts</span>
              </div>
              <span className="font-bold text-slate-900">{analytics?.conversions?.contacts || 0}</span>
            </div>
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Top Pages</h3>
          {analytics?.top_pages?.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_pages.slice(0, 5).map((page, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-sm text-slate-600 truncate flex-1 mr-2">{page.page || "/"}</span>
                  <span className="text-sm font-medium text-slate-900">{page.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No page data yet</p>
          )}
        </div>

        {/* Traffic Sources */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Traffic Sources</h3>
          {analytics?.traffic_sources?.length > 0 ? (
            <div className="space-y-3">
              {analytics.traffic_sources.slice(0, 5).map((source, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-sm text-slate-600 truncate flex-1 mr-2">
                    {source.source ? new URL(source.source).hostname : "Direct"}
                  </span>
                  <span className="text-sm font-medium text-slate-900">{source.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No referrer data yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Admin Panel Component
export default function AdminPanel() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check for existing auth on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("kineserve_token");
    const storedUser = localStorage.getItem("kineserve_user");
    
    if (storedToken && storedUser) {
      // Verify token is still valid
      verifyToken(storedToken, JSON.parse(storedUser));
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (storedToken, storedUser) => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setToken(storedToken);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      // Token is invalid, clear storage
      localStorage.removeItem("kineserve_token");
      localStorage.removeItem("kineserve_user");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("kineserve_token");
    localStorage.removeItem("kineserve_user");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    toast.success("Signed out successfully");
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  // Create authenticated axios instance
  const authAxios = createAuthAxios(token);

  return (
    <AuthContext.Provider value={{ user, token, authAxios }}>
      <div className="min-h-screen bg-slate-50" data-testid="admin-panel">
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)}
          user={user}
          onLogout={handleLogout}
        />
        
        {/* Mobile header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-30 px-4 py-3 flex items-center justify-between">
          <img 
            src={KINESERVE_LOGO} 
            alt="KineServe" 
            className="h-6"
            onError={(e) => {
              e.target.outerHTML = '<div class="flex items-center gap-1"><div class="bg-slate-900 text-white px-1.5 py-0.5 text-sm font-bold">KS</div></div>';
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </Button>
        </div>
        
        <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
          <div className="p-6 lg:p-8">
            <Routes>
              <Route index element={<Dashboard authAxios={authAxios} />} />
              <Route path="analytics" element={<AnalyticsPage authAxios={authAxios} />} />
              <Route path="tenants" element={<TenantsPage authAxios={authAxios} />} />
              <Route path="demo-requests" element={<DemoRequestsPage authAxios={authAxios} />} />
              <Route path="contacts" element={<ContactsPage authAxios={authAxios} />} />
              <Route path="change-password" element={<ChangePasswordPage authAxios={authAxios} />} />
            </Routes>
          </div>
        </main>
      </div>
    </AuthContext.Provider>
  );
}
